import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from 'react-query';
import { 
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  KeyIcon,
  QuestionMarkCircleIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  BellSlashIcon,
  ClockIcon,
  CalendarDaysIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { 
  BellSlashIcon as BellSlashIconSolid,
  CalendarDaysIcon as CalendarDaysIconSolid
} from '@heroicons/react/24/solid';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import useStatusBarHeight from '../hooks/useStatusBarHeight';

const Profile = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showNotificationMuteModal, setShowNotificationMuteModal] = useState(false);
  const [isLoadingMuteStatus, setIsLoadingMuteStatus] = useState(false);
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [showCustomVacationModal, setShowCustomVacationModal] = useState(false);
  const [vacationForm, setVacationForm] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [noticesCount, setNoticesCount] = useState(0);
  const [helpsCount, setHelpsCount] = useState(0);
  
  // 휴가 상태 조회 (React Query)
  const { data: vacationStatus, refetch: refetchVacationStatus } = useQuery(
    ['vacation-status', user?.id],
    () => api.get('/users/vacation/status').then(res => res.data),
    {
      enabled: !!user,
      refetchInterval: 30000 // 30초마다 자동 새로고침
    }
  );
  
  // 알림 중지 상태 조회 (React Query)
  const { data: notificationMuteStatus } = useQuery(
    ['notification-mute-status'],
    () => api.get('/users/notification-mute/status').then(res => res.data),
    {
      enabled: !!user
    }
  );

  useEffect(() => {
    fetchCounts();
  }, []);

  const fetchCounts = async () => {
    try {
      // 공지사항 개수 가져오기
      const noticesResponse = await api.get('/notices');
      setNoticesCount(noticesResponse.data.total || 0);
      
      // 도움말 개수 가져오기
      const helpsResponse = await api.get('/helps');
      setHelpsCount(helpsResponse.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch counts:', error);
    }
  };

  useEffect(() => {
    if (vacationStatus?.start_date) {
      setVacationForm({
        start_date: vacationStatus.start_date,
        end_date: vacationStatus.end_date,
        reason: vacationStatus.reason || ''
      });
    }
  }, [vacationStatus]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('로그아웃되었습니다');
  };

  const handleNotificationMute = async (duration) => {
    try {
      setIsLoadingMuteStatus(true);
      const response = await api.post('/users/notification-mute', { duration });
      toast.success(response.data.message);
      setShowNotificationMuteModal(false);
      // NotificationStatusBar를 위해 쿼리 무효화
      queryClient.invalidateQueries(['notification-mute-status']);
    } catch (error) {
      toast.error('알림 설정에 실패했습니다.');
    } finally {
      setIsLoadingMuteStatus(false);
    }
  };

  const handleVacationSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/users/vacation', vacationForm);
      toast.success(response.data.message);
      setShowVacationModal(false);
      setShowCustomVacationModal(false);
      setVacationForm({ start_date: '', end_date: '', reason: '' });
      // React Query 캐시 무효화 및 강제 새로고침
      await queryClient.invalidateQueries(['vacation-status']);
      await refetchVacationStatus();
    } catch (error) {
      toast.error(error.response?.data?.error || '휴가 신청에 실패했습니다.');
    }
  };

  const handleVacationCancel = async () => {
    if (window.confirm('휴가를 취소하시겠습니까?')) {
      try {
        const today = new Date().toISOString().split('T')[0];
        await api.post('/users/vacation', {
          start_date: today,
          end_date: today,
          reason: '휴가 취소'
        });
        toast.success('휴가가 취소되었습니다.');
        setShowVacationModal(false);
        setVacationForm({ start_date: '', end_date: '', reason: '' });
        // React Query 캐시 무효화 및 강제 새로고침
        await queryClient.invalidateQueries(['vacation-status']);
        await refetchVacationStatus();
      } catch (error) {
        toast.error('휴가 취소에 실패했습니다.');
      }
    }
  };

  const handleVacationEnd = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await api.post('/users/vacation', {
        start_date: today,
        end_date: today,
        reason: '휴가 종료'
      });
      toast.success('휴가가 종료되었습니다.');
      setShowVacationModal(false);
      setVacationForm({ start_date: '', end_date: '', reason: '' });
      // React Query 캐시 무효화 및 강제 새로고침
      await queryClient.invalidateQueries(['vacation-status']);
      await refetchVacationStatus();
    } catch (error) {
      toast.error('휴가 종료에 실패했습니다.');
    }
  };

  const getUserTypeLabel = (userType) => {
    const labels = {
      admin: '관리자',
      contractor: '소장',
      customer: '고객',
      vip_customer: 'VIP 고객'
    };
    return labels[userType] || userType;
  };

  const getUserTypeBadgeColor = (userType) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      contractor: 'bg-blue-100 text-blue-700',
      customer: 'bg-gray-100 text-gray-700',
      vip_customer: 'bg-yellow-100 text-yellow-700'
    };
    return colors[userType] || 'bg-gray-100 text-gray-700';
  };

  const menuSections = [
    {
      title: '계정 관리',
      items: [
        {
          icon: KeyIcon,
          label: '비밀번호 변경',
          onClick: () => navigate('/profile/change-password'),
          show: true
        },
        {
          icon: BellIcon,
          label: '알림 설정',
          onClick: () => setShowNotificationMuteModal(true),
          show: true,
          badge: notificationMuteStatus?.is_muted ? (
            <BellSlashIconSolid className="h-4 w-4 text-red-500" />
          ) : null
        },
        {
          icon: CalendarDaysIcon,
          label: vacationStatus?.is_on_vacation ? '휴가 관리' : '휴가 신청',
          onClick: () => setShowVacationModal(true),
          show: user?.user_type === 'contractor' || user?.user_type === 'admin',
          badge: vacationStatus?.is_on_vacation ? (
            <CalendarDaysIconSolid className="h-4 w-4 text-blue-500" />
          ) : null
        }
      ]
    },
    {
      title: '지원',
      items: [
        {
          icon: QuestionMarkCircleIcon,
          label: '도움말',
          onClick: () => navigate('/help'),
          badge: helpsCount > 0 ? helpsCount.toString() : null,
          show: true
        },
        {
          icon: ExclamationCircleIcon,
          label: '공지사항',
          onClick: () => navigate('/notices'),
          badge: noticesCount > 0 ? noticesCount.toString() : null,
          show: true
        }
      ]
    }
  ];

  const statusBarHeight = useStatusBarHeight();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white sticky z-30 shadow-sm" style={{ top: `${statusBarHeight}px` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <UserCircleIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  내 프로필
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  계정 정보 및 설정
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 프로필 정보 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                {user?.name ? (
                  <span className="text-2xl font-bold text-white">
                    {user.name[0]}
                  </span>
                ) : (
                  <UserCircleIcon className="h-10 w-10 text-white" />
                )}
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{user?.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-lg ${getUserTypeBadgeColor(user?.user_type)}`}>
                  {getUserTypeLabel(user?.user_type)}
                </span>
                {user?.user_type === 'vip_customer' && (
                  <SparklesIcon className="h-4 w-4 text-yellow-500" />
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
            {(user?.user_type === 'admin' || user?.user_type === 'contractor') && user?.login_id && (
              <div className="flex items-center gap-3">
                <UserCircleIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">ID: {user.login_id}</span>
              </div>
            )}
            {user?.email && (
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{user.email}</span>
              </div>
            )}
            {user?.phone && (
              <div className="flex items-center gap-3">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{user.phone}</span>
              </div>
            )}
            {user?.company_name && (
              <div className="flex items-center gap-3">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">{user.company_name}</span>
              </div>
            )}
            {user?.specialties && (
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <span className="text-sm text-gray-600">{user.specialties}</span>
              </div>
            )}
          </div>
        </div>

        {/* VIP 정보 */}
        {user?.user_type === 'vip_customer' && user?.vip_info && (
          <div className="mt-4">
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-yellow-600" />
                  VIP 리워드
                </h3>
                <button
                  onClick={() => navigate('/vip')}
                  className="text-sm text-yellow-700 font-medium hover:text-yellow-800"
                >
                  자세히 보기
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">현재 포인트</p>
                  <p className="text-lg font-bold text-gray-900">
                    {new Intl.NumberFormat('ko-KR').format(user.vip_info.current_balance || 0)}P
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">누적 적립</p>
                  <p className="text-lg font-bold text-gray-900">
                    {new Intl.NumberFormat('ko-KR').format(user.vip_info.total_earned || 0)}P
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 메뉴 섹션 */}
        <div className="mt-6 space-y-6">
          {menuSections.map((section, index) => (
            <div key={index}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-3">
                {section.title}
              </h3>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                {section.items.filter(item => item.show).map((item, itemIndex) => (
                  <button
                    key={itemIndex}
                    onClick={item.onClick}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-all duration-200 first:rounded-t-xl last:rounded-b-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                        <item.icon className="h-5 w-5 text-gray-700" />
                      </div>
                      <span className="font-medium text-gray-900">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.badge && (
                        typeof item.badge === 'string' ? (
                          <span className="bg-gray-100 text-gray-700 text-xs font-medium rounded-lg px-2.5 py-1">
                            {item.badge}
                          </span>
                        ) : (
                          item.badge
                        )
                      )}
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* 로그아웃 버튼 */}
        <button
          onClick={() => setShowLogoutModal(true)}
          className="w-full mt-8 bg-white rounded-xl border border-red-200 p-4 flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 transition-all duration-200"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
          <span className="font-medium">로그아웃</span>
        </button>

        {/* 버전 정보 */}
        <div className="mt-12 pb-8 text-center">
          <div className="inline-flex flex-col items-center">
            <p className="text-xs text-gray-400 font-medium">
              건기 프로젝트
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Version 1.0.0
            </p>
            <p className="text-xs text-gray-400 mt-2">
              © 2024 Geongi Platform
            </p>
          </div>
        </div>
      </div>

      {/* 로그아웃 확인 모달 */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
            <div className="p-6 text-center">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500 rounded-full blur-2xl opacity-20"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <ArrowRightOnRectangleIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">로그아웃 하시겠습니까?</h3>
              <p className="text-sm text-gray-500">
                다시 로그인하려면 계정 정보가 필요합니다.
              </p>
            </div>
            <div className="flex gap-3 p-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors shadow-sm border border-gray-200"
              >
                취소
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors shadow-sm"
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 알림 중지 설정 모달 */}
      {showNotificationMuteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500 rounded-full blur-2xl opacity-20"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <BellSlashIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                알림 일시 중지
              </h3>
              
              {notificationMuteStatus?.is_muted ? (
                <div className="mb-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-orange-800 mb-1">
                      🔕 현재 알림이 중지된 상태입니다
                    </p>
                    <p className="text-xs text-orange-600">
                      남은 시간: <span className="font-bold">{notificationMuteStatus.remaining_minutes}분</span>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('알림 중지를 해제하시겠습니까?')) {
                        handleNotificationMute(0);
                      }
                    }}
                    className="w-full px-4 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors"
                  >
                    지금 알림 받기 (중지 해제)
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center mb-4">
                  선택한 시간 동안 SMS와 푸시 알림을 받지 않습니다.
                </p>
              )}
              
              <div className="space-y-2">
                {[
                  { duration: 30, label: '30분' },
                  { duration: 60, label: '1시간' },
                  { duration: 90, label: '1시간 30분' },
                  { duration: 120, label: '2시간' }
                ].map((option) => (
                  <button
                    key={option.duration}
                    onClick={() => handleNotificationMute(option.duration)}
                    disabled={isLoadingMuteStatus}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <ClockIcon className="h-5 w-5 text-gray-400 group-hover:text-orange-500" />
                      <span className="font-medium text-gray-700">{option.label} 동안 알림 받지 않음</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                  </button>
                ))}
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                * 시스템 내 알림은 정상적으로 표시됩니다.
              </p>
            </div>
            
            <div className="flex gap-3 p-4 bg-gray-50 rounded-b-3xl">
              <button
                onClick={() => setShowNotificationMuteModal(false)}
                className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors shadow-sm"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 휴가 설정 모달 */}
      {showVacationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[90vh] relative z-[10000]">
            {/* 헤더 - 고정 */}
            <div className="p-6 pb-0 flex-shrink-0">
              <button
                onClick={() => setShowVacationModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500 rounded-full blur-2xl opacity-20"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CalendarDaysIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                휴가 설정
              </h3>
            </div>
            
            {/* 콘텐츠 - 스크롤 가능 */}
            <div className="p-6 pt-2 overflow-y-auto flex-1">
              {vacationStatus?.is_on_vacation ? (
                <div className="mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-blue-800 mb-1">
                      🏖️ 현재 휴가 중입니다
                    </p>
                    <p className="text-xs text-blue-600">
                      남은 시간: <span className="font-bold">{vacationStatus.days_remaining}일</span>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('휴가를 종료하시겠습니까?')) {
                        handleVacationEnd();
                      }
                    }}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                  >
                    지금 업무 복귀 (휴가 종료)
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center mb-4">
                  휴가 기간을 선택하세요.
                </p>
              )}
              
              {!vacationStatus?.is_on_vacation && (
                <div className="space-y-2">
                  {[
                    { days: 1, label: '1일' },
                    { days: 3, label: '3일' },
                    { days: 7, label: '1주일' },
                    { days: 14, label: '2주일' }
                  ].map((option) => (
                    <button
                      key={option.days}
                      onClick={() => {
                        const startDate = new Date();
                        const endDate = new Date();
                        endDate.setDate(endDate.getDate() + option.days - 1);
                        
                        setVacationForm({
                          start_date: startDate.toISOString().split('T')[0],
                          end_date: endDate.toISOString().split('T')[0],
                          reason: ''
                        });
                        setShowVacationModal(false);
                        
                        // 자동으로 휴가 신청
                        api.post('/users/vacation', {
                          start_date: startDate.toISOString().split('T')[0],
                          end_date: endDate.toISOString().split('T')[0],
                          reason: ''
                        }).then(async () => {
                          toast.success(`${option.label} 휴가가 신청되었습니다.`);
                          // React Query 캐시 무효화 및 강제 새로고침
                          await queryClient.invalidateQueries(['vacation-status']);
                          await refetchVacationStatus();
                        }).catch((error) => {
                          console.error('Vacation request error:', error);
                          toast.error('휴가 신청에 실패했습니다.');
                        });
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <CalendarDaysIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500" />
                        <span className="font-medium text-gray-700">{option.label} 동안 휴가</span>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                    </button>
                  ))}
                  
                  <button
                    onClick={() => {
                      setShowVacationModal(false);
                      setShowCustomVacationModal(true);
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <CalendarDaysIcon className="h-5 w-5 text-gray-400 group-hover:text-blue-500" />
                      <span className="font-medium text-gray-700">직접 날짜 선택</span>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                  </button>
                </div>
              )}
              
              <p className="text-xs text-gray-500 text-center mt-4">
                * 휴가 중에는 프로젝트 배정이 제한됩니다.
              </p>
            </div>
            
            <div className="flex gap-3 p-4 bg-gray-50 rounded-b-3xl">
              <button
                onClick={() => setShowVacationModal(false)}
                className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors shadow-sm"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 커스텀 휴가 날짜 선택 모달 */}
      {showCustomVacationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[90vh] relative z-[10000]">
            <form onSubmit={handleVacationSubmit} className="flex flex-col h-full">
              {/* 헤더 - 고정 */}
              <div className="p-6 pb-4 border-b flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomVacationModal(false);
                    setVacationForm({ start_date: '', end_date: '', reason: '' });
                  }}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
                <h3 className="text-lg font-semibold text-gray-900 text-center">커스텀 휴가 기간</h3>
              </div>
              
              {/* 콘텐츠 - 스크롤 가능 */}
              <div className="p-6 pt-4 overflow-y-auto flex-1">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      휴가 시작일
                    </label>
                    <input
                      type="date"
                      value={vacationForm.start_date}
                      onChange={(e) => setVacationForm({ ...vacationForm, start_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      휴가 종료일
                    </label>
                    <input
                      type="date"
                      value={vacationForm.end_date}
                      onChange={(e) => setVacationForm({ ...vacationForm, end_date: e.target.value })}
                      min={vacationForm.start_date || new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      휴가 사유 (선택)
                    </label>
                    <input
                      type="text"
                      value={vacationForm.reason}
                      onChange={(e) => setVacationForm({ ...vacationForm, reason: e.target.value })}
                      placeholder="예: 개인 사정, 가족 행사 등"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* 푸터 - 고정 */}
              <div className="p-6 pt-4 border-t bg-gray-50 flex-shrink-0 space-y-2">
                <button
                  type="submit"
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                >
                  휴가 신청
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomVacationModal(false);
                    setVacationForm({ start_date: '', end_date: '', reason: '' });
                  }}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;